import React, {Fragment, memo} from 'react';
import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));
import {AnimatedComponent} from '../AnimatedComponent';
import {fadeInUp} from '@/animation/scroll-animation';
import {colors, spacing} from '@/styles/tokens';

// Migrated from globals.scss `.platforms-content` (+ its 600px padding override), exclusively owned by this component.
const sxPlatformsContent = {
    border: `1px solid ${colors.text.inverse}`,
    height: '100%',
    padding: spacing[20],
    boxShadow: '0 0 8px rgb(0 0 0 / 0.1)',
    '@media (max-width: 600px)': {padding: '20px 0'}
} as const;

interface PlatformsCard {
    icon: StaticImageData;
    heading: string;
    body?: string;
    redirect: string;
}

interface PlatformsProps {
    data?: PlatformsCard[];
    heading: React.ReactNode;
    subheading?: string;
    className?: string;
    containerClass?: string;
    showBorder?: boolean;
    cardSx?: SxProps<Theme>;
}

const Platforms = (props: PlatformsProps) => {
    const {data, heading, subheading, className, showBorder, cardSx} = props;
    return (
        <Box className="one-step-component-wrapper">
            <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                    {heading}
                </Typography>
                {subheading && (
                    <Typography variant="subtitle1" component="p" className="font-regular-text  remove-underline" sx={{mb: {xs: 2, md: 6}}}>
                        {parse(subheading)}
                    </Typography>
                )}
            </Box>
            <MUIGrid container spacing={2} className="business-grid-wrapper">
                {data?.map((subData: PlatformsCard, index: number) => (
                    <Fragment key={index + 1}>
                        <MUIGrid item xs={12} sm={6} md={4} lg={4}>
                            <AnimatedComponent variants={fadeInUp} delay={index * 0.2} className="height-full">
                                <Box
                                    className={className}
                                    sx={[sxPlatformsContent, cardSx] as SxProps<Theme>}
                                    style={showBorder ? {border: '2px solid #F5F5F9', borderRadius: '15px'} : undefined}
                                >
                                    <Box>
                                        <DisplayImage src={subData.icon} alt={subData.heading} priority={false} className="step-image" />
                                    </Box>
                                    <Box sx={{marginTop: '15px'}}>
                                        {subData.redirect !== '' ? (
                                            <Link
                                                href={subData?.redirect}
                                                target="_blank"
                                                className="services_link menu-links hover-underline-text"
                                            >
                                                <Typography className="one-step-content-heading " component="h3">
                                                    {subData.heading}
                                                </Typography>
                                            </Link>
                                        ) : (
                                            <Typography className="one-step-content-heading " component="h3">
                                                {subData.heading}
                                            </Typography>
                                        )}
                                        <Typography className="one-step-sub-heading  remove-underline ">
                                            {subData.body ? parse(subData.body) : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            </AnimatedComponent>
                        </MUIGrid>
                    </Fragment>
                ))}
            </MUIGrid>
        </Box>
    );
};
export default memo(Platforms);
